import type { ReactNode } from 'react';

import { descricaoDoGrupo, estadoDoGrupo, rotuloDoGrupo } from '@/features/acoes/grupos';
import type { AcaoDetalhada, GrupoDeCampo } from '@/types/api';

import estilos from './GrupoDeCampos.module.css';

/**
 * Seção de um grupo de campo, aberta para escrita ou fechada com o motivo.
 *
 * POR QUE O MOTIVO APARECE NA TELA
 *
 * Campo cinza sem explicação vira chamado de suporte. "Não consigo editar o assunto" não
 * tem resposta até alguém abrir o código, e a pessoa conclui que o sistema está com
 * defeito. O texto vem do servidor porque a explicação é parte da regra: quem sabe que a
 * saída é "solicite ajustes ao CS" é o domínio.
 *
 * DUAS CAUSAS, DUAS SAÍDAS. Situação fechada se resolve esperando ou transicionando;
 * falta de permissão se resolve com um administrador. Misturar as duas produziria a
 * orientação errada na metade dos casos.
 *
 * O conteúdo de leitura é sempre renderizado. Um grupo fechado não esconde o que já está
 * gravado: esconder faria a ação parecer vazia para quem não pode editá-la.
 */

interface Props {
  acao: AcaoDetalhada;
  grupo: GrupoDeCampo;
  /** Conteúdo quando o grupo aceita escrita. */
  children: (editavel: boolean) => ReactNode;
}

export function GrupoDeCampos({ acao, grupo, children }: Props) {
  const { editavel, bloqueio } = estadoDoGrupo(acao, grupo);

  return (
    <section className={estilos.grupo} aria-label={rotuloDoGrupo[grupo]}>
      <header className={estilos.cabecalho}>
        <div>
          <h3 className={estilos.titulo}>{rotuloDoGrupo[grupo]}</h3>
          <p className={estilos.descricao}>{descricaoDoGrupo[grupo]}</p>
        </div>

        {!editavel && (
          <span
            className={
              bloqueio?.porPermissao === true
                ? `${estilos.selo} ${estilos.seloDePermissao}`
                : estilos.selo
            }
          >
            {/* O selo diz o ESTADO, e a mensagem abaixo diz a razao. Tentar caber a razao
                num selo produziria um selo de tres linhas. */}
            Somente leitura
          </span>
        )}
      </header>

      {bloqueio !== undefined && (
        <p className={estilos.motivo}>
          {bloqueio.motivo}
          {/* Quando a causa e permissao, a acao nao muda nada: dizer isso evita a pessoa
              ficar esperando uma transicao que nao vai destravar o campo. */}
          {bloqueio.porPermissao && (
            <span className={estilos.nota}> Transicionar a ação não muda isso.</span>
          )}
        </p>
      )}

      <div className={estilos.corpo}>{children(editavel)}</div>
    </section>
  );
}
